import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Empty, Input, Modal, Skeleton, Tag, Typography, message as antMessage } from 'antd';
import type { DeadLetterSummary, WebhookDeadLetterSummary } from '@youkuai/contracts';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from './auth.tsx';
import { listDeadLetters, listWebhookDeadLetters, replayDeadLetter, replayWebhookDeadLetter } from './outbox-api.ts';
import { ApiRequestError } from './project-api.ts';

const { Title } = Typography;

function errorText(error: unknown): string {
  if (error instanceof ApiRequestError) {
    if (error.code === 'FORBIDDEN') return '管理员权限已变化，请重新登录核对。';
    if (error.code === 'VERSION_CONFLICT' || error.code === 'STATE_CONFLICT') return '事件状态已变化，请刷新列表再确认。';
    if (error.code === 'UNSUPPORTED_TOPIC') return '当前版本尚无此主题的消费者，不能重放。';
    return `操作失败（${error.code}），请核对后重试。`;
  }
  return '操作失败，请稍后重试。';
}

export function OutboxOperationsPage() {
  const { state } = useAuth();
  const token = state.kind === 'ready' ? state.token : '';
  const userId = state.kind === 'ready' ? state.session.userId : '';
  const isAdmin = state.kind === 'ready' && state.session.roles.includes('ADMIN');
  const client = useQueryClient();
  const [selected, setSelected] = useState<DeadLetterSummary | null>(null);
  const [selectedWebhook, setSelectedWebhook] = useState<WebhookDeadLetterSummary | null>(null);
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const attempt = useRef<{ payload: string; key: string } | null>(null);
  const webhookAttempt = useRef<{ payload: string; key: string } | null>(null);
  const query = useInfiniteQuery({ queryKey: ['outbox-dead-letters', userId], initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => listDeadLetters(token, pageParam ?? undefined),
    getNextPageParam: (page) => page.nextCursor ?? undefined, enabled: !!token && isAdmin });
  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];
  const webhookQuery = useInfiniteQuery({ queryKey: ['webhook-dead-letters', userId],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => listWebhookDeadLetters(token, pageParam ?? undefined),
    getNextPageParam: (page) => page.nextCursor ?? undefined, enabled: !!token && isAdmin });
  const webhookRows = webhookQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const replay = useMutation({ mutationFn: (input: { messageId: string; expectedGeneration: number;
    reason: string; key: string }) => replayDeadLetter(token, input) });
  const replayWebhook = useMutation({ mutationFn: (input: { deliveryId: string; expectedGeneration: number;
    reason: string; key: string }) => replayWebhookDeadLetter(token, input) });

  async function submit() {
    if (!selected || !selected.replayable || reason.trim().length < 10) return;
    const payload = JSON.stringify({ messageId: selected.messageId, generation: selected.replayGeneration, reason: reason.trim() });
    if (attempt.current?.payload !== payload) attempt.current = { payload, key: `outbox-replay-${crypto.randomUUID()}` };
    try {
      await replay.mutateAsync({ messageId: selected.messageId, expectedGeneration: selected.replayGeneration,
        reason: reason.trim(), key: attempt.current.key });
      attempt.current = null; setSelected(null); setReason('');
      await client.invalidateQueries({ queryKey: ['outbox-dead-letters', userId] });
      setNotice({ type: 'success', message: '已排入重试队列；这不是下游处理成功的保证，请继续观察。' });
    } catch (error) {
      const message = errorText(error); setNotice({ type: 'error', message }); void antMessage.error(message); throw error;
    }
  }

  async function submitWebhook() {
    if (!selectedWebhook || reason.trim().length < 10) return;
    const payload = JSON.stringify({ deliveryId: selectedWebhook.deliveryId,
      generation: selectedWebhook.replayGeneration, reason: reason.trim() });
    if (webhookAttempt.current?.payload !== payload) {
      webhookAttempt.current = { payload, key: `webhook-replay-${crypto.randomUUID()}` };
    }
    try {
      await replayWebhook.mutateAsync({ deliveryId: selectedWebhook.deliveryId,
        expectedGeneration: selectedWebhook.replayGeneration, reason: reason.trim(), key: webhookAttempt.current.key });
      webhookAttempt.current = null; setSelectedWebhook(null); setReason('');
      await client.invalidateQueries({ queryKey: ['webhook-dead-letters', userId] });
      setNotice({ type: 'success', message: '外部投递已使用原 delivery ID 重新排队；请继续观察独立送达回执。' });
    } catch (error) {
      const message = errorText(error); setNotice({ type: 'error', message }); void antMessage.error(message); throw error;
    }
  }

  return <main className="page-shell customer-shell">
    <Link className="back-link" to="/projects">← 返回我的项目</Link>
    <div className="section-kicker">OPERATIONS / 事件恢复</div>
    <div className="project-heading"><div><Title level={1}>Outbox 死信</Title>
      <p>仅供管理员逐条核查和受控重试；不会展示业务载荷或把重放当作交付成功。</p></div></div>
    {!isAdmin ? <Alert type="error" showIcon title="当前账号无运维重放权限" /> : <>
      {notice && <Alert className="workspace-alert" showIcon type={notice.type} title={notice.message} closable
        onClose={() => setNotice(null)} />}
      <Button onClick={() => { void query.refetch(); void webhookQuery.refetch(); }}
        loading={query.isFetching || webhookQuery.isFetching}>刷新状态</Button>
      <Title level={2}>内部活动投影</Title>
      {query.isPending && <Skeleton active paragraph={{ rows: 4 }} />}
      {query.isError && <Alert type="error" showIcon title="死信读取失败" description={errorText(query.error)}
        action={<Button onClick={() => void query.refetch()}>重试</Button>} />}
      {!query.isPending && !query.isError && rows.length === 0 && <Empty description="当前没有待处理死信" />}
      <div className="customer-rows">{rows.map((row) => <article className="customer-row" key={row.messageId}>
        <div><span className="customer-code">{row.topic} · 第 {row.attemptCount} 次失败</span><h3>{row.aggregateType} / {row.aggregateId}</h3>
          <p>消息 {row.messageId} · {row.deadLetteredAt} · {row.failureCode ?? '未分类'}</p>
          <Tag color={row.replayable ? 'warning' : 'error'}>{row.replayable ? '可核查重放' : '无可用消费者'}</Tag></div>
        <Button disabled={!row.replayable} onClick={() => { setSelected(row); setReason(''); setNotice(null); }}>查看影响并重放</Button>
      </article>)}</div>
      {query.hasNextPage && <Button loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
        {query.isFetchNextPageError ? '重试加载' : '继续加载'}</Button>}
      <Title level={2}>外部 Webhook 投递</Title>
      <p>状态与内部投影相互独立；列表不会显示目标 URL、密钥、业务载荷或响应正文。</p>
      {webhookQuery.isPending && <Skeleton active paragraph={{ rows: 3 }} />}
      {webhookQuery.isError && <Alert type="error" showIcon title="外部投递死信读取失败"
        description={errorText(webhookQuery.error)} action={<Button onClick={() => void webhookQuery.refetch()}>重试</Button>} />}
      {!webhookQuery.isPending && !webhookQuery.isError && webhookRows.length === 0 &&
        <Empty description="当前没有外部投递死信" />}
      <div className="customer-rows">{webhookRows.map((row) => <article className="customer-row" key={row.deliveryId}>
        <div><span className="customer-code">{row.consumerName} · {row.topic} · 第 {row.attemptCount} 次失败</span>
          <h3>{row.aggregateType} / {row.aggregateId}</h3>
          <p>投递 {row.deliveryId} · 消息 {row.messageId} · {row.failureCode ?? '未分类'}</p>
          <Tag color="warning">独立回执待恢复</Tag></div>
        <Button onClick={() => { setSelectedWebhook(row); setReason(''); setNotice(null); }}>
          查看影响并重放</Button>
      </article>)}</div>
      {webhookQuery.hasNextPage && <Button loading={webhookQuery.isFetchingNextPage}
        onClick={() => void webhookQuery.fetchNextPage()}>
        {webhookQuery.isFetchNextPageError ? '重试加载' : '继续加载'}</Button>}
      <Modal title="重放影响预览" open={!!selected} footer={null} destroyOnHidden
        onCancel={() => { setSelected(null); setReason(''); attempt.current = null; }}>
        <p>仅重新排队这 1 条消息（{selected?.topic}）；涉及对象 {selected?.aggregateId}。</p>
        <Alert type="warning" showIcon title="本次不直接记账，也不保证下游成功" description="金额影响未知；重放后请观察消费回执。失败历史与审计仍保留。" />
        {notice?.type === 'error' && <Alert type="error" showIcon title={notice.message} />}
        <p>请填写故障已排查或修复的依据（至少 10 字）：</p>
        <Input.TextArea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} rows={3} />
        <div className="customer-modal-actions"><Button onClick={() => setSelected(null)}>取消</Button>
          <Button type="primary" disabled={reason.trim().length < 10 || replay.isPending} loading={replay.isPending}
            onClick={() => Modal.confirm({ title: '二次确认：重新排队 1 条消息？',
              content: `主题 ${selected?.topic}；金额影响未知。确认只排队，不增加审批人。`,
              okText: '确认重放', onOk: submit })}>核对后继续</Button></div>
      </Modal>
      <Modal title="外部投递重放影响预览" open={!!selectedWebhook} footer={null} destroyOnHidden
        onCancel={() => { setSelectedWebhook(null); setReason(''); webhookAttempt.current = null; }}>
        <p>仅重新排队 1 条投递（{selectedWebhook?.consumerName} / {selectedWebhook?.topic}）。</p>
        <Alert type="warning" showIcon title="沿用原 delivery ID，金额影响未知"
          description="接收端必须按 delivery ID 去重；失败历史与审计保留，本操作不保证接收端最终处理成功。" />
        {notice?.type === 'error' && <Alert type="error" showIcon title={notice.message} />}
        <p>请填写接收端已恢复或故障已排查的依据（至少 10 字）：</p>
        <Input.TextArea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} rows={3} />
        <div className="customer-modal-actions"><Button onClick={() => setSelectedWebhook(null)}>取消</Button>
          <Button type="primary" disabled={reason.trim().length < 10 || replayWebhook.isPending}
            loading={replayWebhook.isPending} onClick={() => Modal.confirm({
              title: '二次确认：重新排队 1 条外部投递？',
              content: `消费者 ${selectedWebhook?.consumerName}；沿用原 delivery ID，金额影响未知。`,
              okText: '确认重放', onOk: submitWebhook })}>核对后继续</Button></div>
      </Modal>
    </>}
  </main>;
}
